import type { NotificationOutput } from "@orsane/schema/notifications";
import { useMutation } from "@tanstack/react-query";
import { produce } from "immer";
import { useTRPC } from "@/lib/trpc";

export const useMarkNotificationAsRead = () => {
    const trpc = useTRPC();

    const opts = trpc.notification.markAsRead.mutationOptions({
        onMutate: async ({ id, isRead }, context) => {
            const queryKey = trpc.notification.list.queryOptions().queryKey;

            // Cancel any outgoing refetches
            await context.client.cancelQueries({ queryKey: trpc.notification.list.queryOptions().queryKey });

            // Mutate optimistically
            const previousNotifications = context.client.getQueryData<NotificationOutput[]>(queryKey);
            if (previousNotifications) {
                const updatedNotifications = produce(previousNotifications, (draft) => {
                    const notificationIndex = draft.findIndex((not) => not.id === id);
                    if (notificationIndex !== -1) {
                        draft[notificationIndex].readAt = isRead ? new Date() : null;
                    }
                });
                context.client.setQueryData(queryKey, updatedNotifications);
            }
        },
        onSuccess: (_, _vars, _on, context) => {
            // invalidate badge count
            context.client.invalidateQueries({ queryKey: trpc.notification.notificationCount.queryOptions().queryKey });
        },
    });
    return useMutation(opts);
};

export const useMarkAllNotificationsAsRead = () => {
    const trpc = useTRPC();

    const opts = trpc.notification.markAllAsRead.mutationOptions({
        onSuccess: (_, _vars, _on, context) => {
            context.client.invalidateQueries({ queryKey: trpc.notification.list.queryOptions().queryKey });
            context.client.invalidateQueries({ queryKey: trpc.notification.notificationCount.queryOptions().queryKey });
        },
    });
    return useMutation(opts);
};

export const useRemoveAllNotifications = () => {
    const trpc = useTRPC();

    const opts = trpc.notification.deleteAll.mutationOptions({
        onSuccess: (_, _vars, _on, context) => {
            context.client.invalidateQueries({ queryKey: trpc.notification.list.queryOptions().queryKey });
            context.client.invalidateQueries({ queryKey: trpc.notification.notificationCount.queryOptions().queryKey });
        },
    });
    return useMutation(opts);
};

export const useDeleteNotification = () => {
    const trpc = useTRPC();

    const opts = trpc.notification.delete.mutationOptions({
        onMutate: async ({ id }, context) => {
            const queryKey = trpc.notification.list.queryOptions().queryKey;

            await context.client.cancelQueries({ queryKey });

            const previousNotifications = context.client.getQueryData<NotificationOutput[]>(queryKey);
            if (previousNotifications) {
                const updatedNotifications = previousNotifications.filter((not) => not.id !== id);
                context.client.setQueryData(queryKey, updatedNotifications);
            }
        },
        onSuccess: (_, _vars, _on, context) => {
            context.client.invalidateQueries({ queryKey: trpc.notification.list.queryOptions().queryKey });
            context.client.invalidateQueries({ queryKey: trpc.notification.notificationCount.queryOptions().queryKey });
        },
    });

    return useMutation(opts);
};

export const useUndeleteNotification = () => {
    const trpc = useTRPC();

    const opts = trpc.notification.undelete.mutationOptions({
        onSuccess: (_, _vars, _on, context) => {
            context.client.invalidateQueries({ queryKey: trpc.notification.list.queryOptions().queryKey });
            context.client.invalidateQueries({ queryKey: trpc.notification.notificationCount.queryOptions().queryKey });
        },
    });

    return useMutation(opts);
};
